import { addMinutes, format, parseISO } from 'date-fns';
import { type Locale, enUS } from 'date-fns/locale';

export class DateUtils {

  // Cache for the current locale - set by LanguageService
  private static currentLocale: Locale = enUS;

  /**
   * Set the locale for date formatting
   * @param locale The date-fns locale object to use
   */
  static setLocale(locale: Locale): void {
    DateUtils.currentLocale = locale;
  }

  /**
   * Get the current locale
   */
  static getLocale(): Locale {
    return DateUtils.currentLocale;
  }

  static isValidDate(date: Date): boolean {
    return date instanceof Date && !isNaN((date as any));
  }

  static getAgnosticDate(dt: Date | string): Date {
    let date = new Date(dt);
    date = addMinutes(date, date.getTimezoneOffset());

    return date;
  }

  static getAgnosticDateString(dt: Date | string, formatStr?: string): string {
    if (!dt) return undefined;

    const date = dt instanceof Date ? dt : DateUtils.getAgnosticDate(dt);

    return format(date, formatStr || 'PP', { locale: DateUtils.currentLocale });
  }

  static nowAsISOString(): string {
    const now = new Date();
    return now.toISOString();
  }

  /**
   * Format a date string with locale support
   * Equivalent to moment(dateString).format('ll') - short localized date (e.g., "Feb 11, 2026")
   * @param dateString ISO date string or a timestamp in milliseconds (e.g. from getTime())
   * @returns Formatted date string or undefined if input is null/undefined
   */
  static getLocaleDate(dateString: string | number): string | undefined {
    if (dateString == null) return undefined;
    const date = typeof dateString === 'number' ? new Date(dateString) : parseISO(dateString);
    return format(date, 'PP', { locale: DateUtils.currentLocale });
  }

  /**
   * Format a date string with full month name
   * Equivalent to moment(dateString).format('LL') - long localized date (e.g., "February 11, 2026")
   * @param dateString ISO date string
   * @returns Formatted date string or undefined if input is null/undefined
   */
  static getLocaleDateFullMonth(dateString: string): string | undefined {
    if (!dateString) return undefined;
    const date = parseISO(dateString);
    return format(date, 'PPPP', { locale: DateUtils.currentLocale });
  }

  /**
   * Format a date string with date and time
   * Equivalent to moment(dateString).format('lll') - short localized date and time (e.g., "Feb 11, 2026, 3:30 PM")
   * @param dateString ISO date string
   * @returns Formatted date string or undefined if input is null/undefined
   */
  static getLocaleDateTime(dateString: string): string | undefined {
    if (!dateString) return undefined;
    const date = parseISO(dateString);
    return format(date, 'PPpp', { locale: DateUtils.currentLocale });
  }

}
